
import React, { useState, useEffect, memo } from 'react';
import { Plus, Pencil } from 'lucide-react';
import CustomizationModal from '../CustomizationModal';
import SpiceLevel from '../SpiceLevel';
import ItemAlreadyInCartDialog from '../ItemAlreadyInCartDialog';
import { settingsService } from '../../config/settingsService';
import { getRestaurantConfigSync } from '../../config/restaurantConfig';
import FallbackImg from '../FallbackImg';



const ProductCard = ({ product, onAddToCart, onShowDetails, isUserLoggedIn, onShowAuthModal, cart = [], onUpdateQuantity, onRemove, generateCartItemKey, outletType }) => {
  const [showCustomize, setShowCustomize] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [modalProduct, setModalProduct] = useState(null);
  const [isModalReady, setIsModalReady] = useState(false);
  const [showItemAlreadyDialog, setShowItemAlreadyDialog] = useState(false);
  const [selectedCustomizableItem, setSelectedCustomizableItem] = useState(null);
  
  // ✅ Find product in cart
  const getCartItemKey = () => {
    if (!product) return null;
    // Generate key using the same logic as RestaurantMenuApp
    const parts = [product.id];

    // Add variation IDs (sorted for consistency)
    if (product.variations && Array.isArray(product.variations) && product.variations.length > 0) {
      const variationIds = product.variations
        .map(v => v.variation_id || v.id)
        .filter(id => id !== undefined)
        .sort()
        .join('-');
      if (variationIds) parts.push(`v:${variationIds}`);
    }

    // Add extra IDs (sorted for consistency)
    if (product.extras && Array.isArray(product.extras) && product.extras.length > 0) {
      const extraIds = product.extras
        .map(e => e.extra_id || e.id)
        .filter(id => id !== undefined)
        .sort()
        .join('-');
      if (extraIds) parts.push(`e:${extraIds}`);
    }

    // Add addon IDs (sorted for consistency)
    if (product.addons && Array.isArray(product.addons) && product.addons.length > 0) {
      const addonIds = product.addons
        .map(a => a.addon_item_id || a.id)
        .filter(id => id !== undefined)
        .sort()
        .join('-');
      if (addonIds) parts.push(`a:${addonIds}`);
    }

    return parts.join('|');
  };
  
  const cartItemKey = getCartItemKey();
  const cartItem = cartItemKey ? cart.find(item => item.cartItemKey === cartItemKey) : null;
  
  // ✅ Check if product needs customization (MUST be before cart quantity calculation)
  const hasVariations = product.variations && 
    typeof product.variations === 'object' && 
    Object.keys(product.variations).length > 0;
  
  const hasExtras = product.extras && 
    Array.isArray(product.extras) && 
    product.extras.length > 0;
  
  const hasAddons = product.addons && 
    Array.isArray(product.addons) && 
    product.addons.length > 0;
  
  const hasModifierGroups = product.modifierGroups && 
    Array.isArray(product.modifierGroups) && 
    product.modifierGroups.length > 0;

  const hasComboGroups = product.comboGroups && 
    Array.isArray(product.comboGroups) && 
    product.comboGroups.length > 0;

  const needsCustomization = hasVariations || hasExtras || hasAddons || hasModifierGroups || hasComboGroups || !!product.isCombo;
  
  // ✅ Check if product is weight-based (unit_type: 2 or weight_config.is_weight_based)
  const isWeightBased = product.isWeightBased || product.unit_type === 2 || product.weight_config?.is_weight_based === true;
  
  // ✅ Get ALL items for this product (all variants)
  const getAllItemsForProduct = () => {
    return cart.filter(item => {
      const itemId = String(item.id || item.item_id || '');
      const productId = String(product.id || '');
      return itemId === productId && productId !== '';
    });
  };
  
  // ✅ Get LAST item in cart for this product (for LIFO decrease)
  const getLastItemForProduct = () => {
    const allItems = getAllItemsForProduct();
    return allItems.length > 0 ? allItems[allItems.length - 1] : null;
  };
  
  // ✅ Check if ANY variant of this product is in cart (for customizable items)
  const findAnyExistingCustomization = () => {
    return cart.find(item => {
      const itemId = String(item.id || item.item_id || '');
      const productId = String(product.id || '');
      return itemId === productId && productId !== '';
    });
  };
  
  // ✅ For customizable products, use any match; for simple, use exact match
  const anyCustomizationInCart = needsCustomization ? findAnyExistingCustomization() : null;
  const relevantCartItem = needsCustomization ? anyCustomizationInCart : cartItem;
  
  // ✅ Calculate TOTAL quantity for all variants of this product
  const getTotalCartQuantity = () => {
    if (needsCustomization) {
      // For customizable products, sum all variants
      return getAllItemsForProduct().reduce((sum, item) => sum + (item.quantity || 0), 0);
    } else {
      // For simple products, just use the single item quantity
      return cartItem?.quantity || 0;
    }
  };
  
  const cartQuantity = isWeightBased ? getWeightBasedCartInfo().totalQuantity : getTotalCartQuantity();
  
  // ✅ Get weight-based cart info
  function getWeightBasedCartInfo() {
    if (!product || !cart) return { totalQuantity: 0, items: [] };
    
    const weightItems = cart.filter(item => {
      const itemId = String(item.id || item.item_id || '');
      const productId = String(product.id || '');
      const itemName = (item.name || '').toLowerCase().trim();
      const productName = (product.name || '').toLowerCase().trim();
      
      // Match by ID primarily, fallback to Name for maximum reliability
      const idMatch = itemId !== '' && itemId === productId;
      const nameMatch = productName !== '' && itemName === productName;
      
      return idMatch || nameMatch;
    });

    const totalQuantity = weightItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
    return { totalQuantity, items: weightItems };
  }
  
  // ✅ Reset modal state when it closes
  useEffect(() => {
    if (!showCustomize) {
      const timer = setTimeout(() => {
        setModalProduct(null);
        setIsModalReady(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [showCustomize]);

  // ✅ Clear dialog state when cart is cleared
  useEffect(() => {
    if (cart.length === 0) {
      setShowItemAlreadyDialog(false);
      setSelectedCustomizableItem(null);
    }
  }, [cart.length]);
  
  const isInventoryCheck = settingsService.isInventoryCheck();
  const isTracked = product.is_tracked || false;
  const availableQty = product.available_stock;
  
  // Use /web/item's available_stock as the source of truth for static display.
  // When inventory check is ON and item is tracked:
  //   - available_stock === 0 → OUT OF STOCK (block button)
  //   - available_stock > 0 → Allow ADD, let check-item API validate at add-time
  const isOutOfStock = isInventoryCheck
    ? (isTracked && (availableQty === 0 || availableQty === '0'))
    : (isTracked && !product.is_available);
  const isLimitedStock = isInventoryCheck && isTracked && !isOutOfStock && availableQty !== null && availableQty > 0 && availableQty <= 5;
  
  const displayPrice = parseFloat(product.price || product.flatPrice || 0);

  const handleCardClick = (e) => {
    // ✅ Prevent handling if clicking on buttons or controls
    if (e.target.closest('.menu-app-add-btn') || e.target.closest('.menu-app-product-qty-controls') || e.target.closest('.menu-app-qty-btn')) {
      return;
    }
    
    if (product.isCombo) {
      console.log('🎁 Card clicked - delegating combo handling to parent:', product.name);
      onAddToCart(product);
    } else if (onShowDetails) {
      onShowDetails(product);
    }
  };
  
  // ✅ Removed local openModal - delegating to parent via onAddToCart
  
  const handleDirectAdd = (e) => {
    // ✅ CRITICAL: Stop all propagation
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    console.log('🔘 ADD clicked for:', product.name, {
      id: product.id,
      needsCustomization,
      isCombo: product.isCombo,
      hasModifierGroups: !!(product.modifierGroups && product.modifierGroups.length > 0),
      hasComboGroups: !!(product.comboGroups && product.comboGroups.length > 0),
      isLoggedIn: isUserLoggedIn,
      timestamp: Date.now()
    });
    
    // ✅ Validation checks
    if (isOutOfStock) {
      alert(`❌ Sorry, ${product.name} is currently out of stock.`);
      return;
    }

    if (isInventoryCheck && isTracked && availableQty && quantity > availableQty) {
      alert(`❌ Only ${Math.floor(availableQty)} items available!`);
      return;
    }
    
    // ✅ For customizable items, show "Repeat or Create New" if already in cart
    if ((needsCustomization || product.isCombo) && !isWeightBased) {
      const lastVariant = getLastItemForProduct();
      if (lastVariant) {
        console.log('🎨 Item in cart, showing repeat/new dialog for LAST variant:', product.name);
        setSelectedCustomizableItem(lastVariant);
        setShowItemAlreadyDialog(true);
        return;
      }
    }

    // ✅ Unified handling: always delegate to parent for anything complex
    if (needsCustomization || product.isCombo || isWeightBased) {
      console.log('🔘 Product requires selection flow, delegating to parent:', product.name);
      onAddToCart(product);
      return;
    }
    
    // ✅ Handle simple products (no customization needed)
    console.log('➕ Adding simple product to cart:', product.name);
    
    const simpleProduct = {
      id: product.id,
      name: product.name,
      price: displayPrice,
      flatPrice: displayPrice,
      image: product.image,
      isVeg: product.isVeg,
      spiceLevel: product.spiceLevel || 0, // shown on the cart line
      description: product.description || '',
      quantity: 1, // ✅ Always add 1 at a time
      variations: [],
      extras: [],
      addons: [],
      cartItemKey: cartItemKey, // ✅ Add the cart item key
      is_tracked: product.is_tracked || false,
      is_available: product.is_available !== false,
      status: product.status,
      category: product.category
    };
    
    console.log('📦 Simple product object:', simpleProduct);
    
    // ✅ Call the parent's add to cart function
    try {
      onAddToCart(simpleProduct);
      console.log('✅ Simple product added successfully');
    } catch (error) {
      console.error('❌ Error adding simple product:', error);
    }
  };

  const handleCloseCustomize = () => {
    console.log('❌ Closing modal');
    setShowCustomize(false);
    setQuantity(1);
  };

  const handleCustomizeComplete = (customizedProduct, isEditing) => {
    console.log('✅ Adding customized product:', customizedProduct.name);
    onAddToCart(customizedProduct, isEditing);
    setShowCustomize(false);
    setQuantity(1);
  };

  const handleIncreaseQuantity = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // ✅ For customizable items, show "Repeat or Create New" if already in cart
    if ((needsCustomization || product.isCombo) && !isWeightBased) {
      const lastVariant = getLastItemForProduct();
      if (lastVariant) {
        console.log('🎨 Item + clicked, showing repeat/new dialog for LAST variant:', product.name);
        setSelectedCustomizableItem(lastVariant);
        setShowItemAlreadyDialog(true);
        return;
      }
    }

    // ✅ For Weight-based OR Customizable products, + ALWAYS opens the selection flow again
    if (isWeightBased || needsCustomization || product.isCombo) {
      console.log('🔘 Item + clicked - delegating to selecting flow:', product.name);
      onAddToCart(product);
      return;
    }

    // ✅ For simple products, direct quantity increase
    if (onUpdateQuantity) {
      onUpdateQuantity(relevantCartItem?.cartItemKey || cartItemKey, 1);
    }
  };

  const handleDecreaseQuantity = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (onUpdateQuantity) {
      if (needsCustomization) {
        // ✅ For customizable products, use LIFO - decrease the LAST added item first
        const lastItem = getLastItemForProduct();
        if (lastItem) {
          console.log('🔽 LIFO Decrease - removing from last variant:', lastItem.cartItemKey);
          onUpdateQuantity(lastItem.cartItemKey, -1);
        }
      } else if (isWeightBased) {
        // ✅ For weight-based items, use LIFO - decrease the LAST added item first
        const weightInfo = getWeightBasedCartInfo();
        if (weightInfo.items && weightInfo.items.length > 0) {
          const lastItem = weightInfo.items[weightInfo.items.length - 1];
          console.log('🔽 LIFO Decrease weight item:', lastItem.cartItemKey);
          onUpdateQuantity(lastItem.cartItemKey, -1);
        }
      } else {
        // ✅ For simple products, use the exact cartItemKey
        onUpdateQuantity(relevantCartItem?.cartItemKey || cartItemKey, -1);
      }
    }
  };
  
  return (
    <>
      <div 
        className={`menu-app-product-card ${isOutOfStock ? 'out-of-stock' : ''}`}
        onClick={handleCardClick}
        style={{ cursor: 'pointer' }}
      >
        <div className="menu-app-product-image-wrapper">
          <FallbackImg 
            src={product.image} 
            alt={product.name} 
            className="menu-app-product-image"
          />
          
          <div className="menu-app-product-badge">
            {outletType !== 'apparel' && outletType !== 'meat_store' && product.isVeg !== undefined && (
              <div className={`menu-app-badge ${product.isVeg ? 'veg' : 'non-veg'}`}>
                <div className={product.isVeg ? 'veg-dot' : 'non-veg-dot'}></div>
              </div>
            )}
          </div>
          
          <div className="menu-app-product-badge-right">
            {isOutOfStock && (
              <div className="menu-app-stock-badge out-of-stock">
                OUT OF STOCK
              </div>
            )}
            
            {isLimitedStock && !isOutOfStock && (
              <div className="menu-app-stock-badge limited-stock">
                Only {Math.floor(availableQty)} left
              </div>
            )}
            
          </div>
        </div>
        
        <div className="menu-app-product-info">
          <h3 className="menu-app-product-name">{product.name}</h3>

          {product.spiceLevel > 0 && (
            <div style={{ margin: '2px 0 4px' }}>
              <SpiceLevel level={product.spiceLevel} size={12} />
            </div>
          )}

          {product.category_name && (
            <span className="menu-app-product-category">{product.category_name}</span>
          )}

          <div className="menu-app-product-footer">
            <span className="menu-app-product-price">
              {needsCustomization && !product.isCombo && 'From '}
              <span>
                {settingsService.formatPrice(displayPrice)}
                {product.priceUnit && (
                  <span className="menu-app-product-price-unit">/{product.priceUnit}</span>
                )}
              </span>
            </span>
            
            {cartQuantity > 0 ? (
              isWeightBased ? (
                /* ✅ Weight-based items: show weight info + Add More button */
                <div className="menu-app-product-qty-controls weight-controls">
                  <button 
                    onClick={handleDecreaseQuantity}
                    className="menu-app-qty-btn minus"
                    type="button"
                  >
                    −
                  </button>
                  <div className="menu-app-qty-display weight-qty">
                    {cartQuantity}
                  </div>
                  <button 
                    onClick={handleIncreaseQuantity}
                    className="menu-app-qty-btn plus"
                    type="button"
                  >
                    +
                  </button>
                </div>
              ) : (
              <div className="menu-app-product-qty-controls">
                <button 
                  onClick={handleDecreaseQuantity}
                  className="menu-app-qty-btn minus"
                  disabled={cartQuantity <= 0}
                  type="button"
                >
                  −
                </button>
                <div className="menu-app-qty-display">
                  {cartQuantity}
                </div>
                <button 
                  onClick={handleIncreaseQuantity}
                  className="menu-app-qty-btn plus"
                  type="button"
                >
                  +
                </button>
              </div>
              )
            ) : (
              <button 
                onClick={handleDirectAdd}
                className={`menu-app-add-btn ${isOutOfStock ? 'disabled' : ''}`}
                disabled={isOutOfStock}
                type="button"
              >
                {isOutOfStock ? 'Out of Stock' : (
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                    ADD {(needsCustomization || product.isCombo) && !isOutOfStock && <Pencil size={12} strokeWidth={2.5} />}
                  </span>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
      
      {/* ✅ Dialog for when trying to add customizable product or clicking + on customized item */}
      <ItemAlreadyInCartDialog
        isOpen={showItemAlreadyDialog}
        onClose={() => {
          setShowItemAlreadyDialog(false);
          setSelectedCustomizableItem(null);
        }}
        productName={product?.name}
        onRepeatSame={() => {
          // ✅ Repeat Same: Increase quantity of existing customization
          if (selectedCustomizableItem && onUpdateQuantity) {
            console.log('📦 Repeat Same - increasing quantity for:', selectedCustomizableItem.cartItemKey);
            onUpdateQuantity(selectedCustomizableItem.cartItemKey, 1);
          }
          setShowItemAlreadyDialog(false);
          setSelectedCustomizableItem(null);
        }}
        onCreateNew={() => {
          // ✅ Create New: Delegate to parent's onAddToCart flow
          console.log('🎨 Create New - delegating to parent flow for:', product.name);
          setShowItemAlreadyDialog(false);
          setSelectedCustomizableItem(null);
          onAddToCart(product);
        }}
      />
    </>
  );
};

// ✅ SIMPLIFIED memo comparison - only check product ID
ProductCard.displayName = 'ProductCard';

export default memo(ProductCard, (prevProps, nextProps) => {
  // Re-render if product ID changes
  if (prevProps.product.id !== nextProps.product.id) {
    return false;
  }
  
  // Re-render if login status changes
  if (prevProps.isUserLoggedIn !== nextProps.isUserLoggedIn) {
    return false;
  }

  // Re-render if cart changes
  const prevCart = prevProps.cart || [];
  const nextCart = nextProps.cart || [];
  
  // Check if product is customizable (has variations, extras, or addons)
  const hasVariations = prevProps.product.variations && 
    typeof prevProps.product.variations === 'object' && 
    Object.keys(prevProps.product.variations).length > 0;
  
  const hasExtras = prevProps.product.extras && 
    Array.isArray(prevProps.product.extras) && 
    prevProps.product.extras.length > 0;
  
  const hasAddons = prevProps.product.addons && 
    Array.isArray(prevProps.product.addons) && 
    prevProps.product.addons.length > 0;
  
  const hasModifierGroups = prevProps.product.modifierGroups && 
    Array.isArray(prevProps.product.modifierGroups) && 
    prevProps.product.modifierGroups.length > 0;

  const hasComboGroups = prevProps.product.comboGroups && 
    Array.isArray(prevProps.product.comboGroups) && 
    prevProps.product.comboGroups.length > 0;
  
  const isWeightBased = prevProps.product.isWeightBased || prevProps.product.unit_type === 2 || prevProps.product.weight_config?.is_weight_based === true;
  
  const isCustomizable = hasVariations || hasExtras || hasAddons || hasModifierGroups || hasComboGroups || isWeightBased || prevProps.product.isCombo;
  
  if (isCustomizable) {
    // For customizable products, check if ANY item for this product changed
    const prevItemsForProduct = prevCart.filter(item => {
      const itemId = String(item.id || item.item_id || '');
      const productId = String(prevProps.product.id || '');
      const itemName = (item.name || '').toLowerCase().trim();
      const productName = (prevProps.product.name || '').toLowerCase().trim();
      return (itemId !== '' && itemId === productId) || (productName !== '' && itemName === productName);
    });
    const nextItemsForProduct = nextCart.filter(item => {
      const itemId = String(item.id || item.item_id || '');
      const productId = String(nextProps.product.id || '');
      const itemName = (item.name || '').toLowerCase().trim();
      const productName = (nextProps.product.name || '').toLowerCase().trim();
      return (itemId !== '' && itemId === productId) || (productName !== '' && itemName === productName);
    });
    
    // Check if total quantity changed
    const prevTotalQty = prevItemsForProduct.reduce((sum, item) => sum + (item.quantity || 0), 0);
    const nextTotalQty = nextItemsForProduct.reduce((sum, item) => sum + (item.quantity || 0), 0);
    
    if (prevTotalQty !== nextTotalQty) {
      return false; // Re-render if total quantity changed
    }
    
    // Check if number of items changed (e.g., variants added or removed)
    if (prevItemsForProduct.length !== nextItemsForProduct.length) {
      return false; // Re-render if number of variants changed
    }
  } else {
    // For simple products, check exact key match
    const prevKey = prevProps.generateCartItemKey ? prevProps.generateCartItemKey(prevProps.product) : prevProps.product.id?.toString();
    const nextKey = nextProps.generateCartItemKey ? nextProps.generateCartItemKey(nextProps.product) : nextProps.product.id?.toString();
    
    const prevItem = prevCart.find(item => item.cartItemKey === prevKey);
    const nextItem = nextCart.find(item => item.cartItemKey === nextKey);
    
    if ((prevItem?.quantity || 0) !== (nextItem?.quantity || 0)) {
      return false; // Re-render if quantity changed
    }
  }

  // Props are equal, don't re-render
  return true;
});

// // src/components/ProductCard/index.js
// import React, { useState, memo } from 'react';
// import { Plus } from 'lucide-react';
// import CustomizationModal from '../CustomizationModal';
// import ComboDetailsModal from '../ComboDetailsModal';

// const getLowestVariationPrice = (product) => {
//   if (!product.variations || typeof product.variations !== 'object') {
//     return parseFloat(product.price || product.flatPrice || 0);
//   }
  
//   const allVariants = Object.values(product.variations).flat();
  
//   if (allVariants.length === 0) {
//     return parseFloat(product.price || product.flatPrice || 0);
//   }
  
//   const prices = allVariants.map(v => parseFloat(v.price || 0));
//   const lowestPrice = Math.min(...prices);
  
//   return lowestPrice;
// };

// // ✅ SIMPLE VERSION - Try all possible combo structures
// const getComboItemsDisplay = (product) => {
//   // If not a combo, show normal description
//   if (!product.isCombo) {
//     return product.description || '';
//   }
  
//   // Try to find combo items (try different names)
//   let items = product.comboItems || product.combo_items || product.items || [];
  
//   // If no items found, show description
//   if (!items || items.length === 0) {
//     return product.description || '';
//   }
  
//   // Get item names and join with dots
//   const names = items.map(item => {
//     return item.item_name || item.name || 'Item';
//   });
  
//   return names.join(' • ');
// };

// const ProductCard = memo(({ product, onAddToCart, onShowDetails, isUserLoggedIn, onShowAuthModal}) => {
//   const [showCustomize, setShowCustomize] = useState(false);
//     const [quantity, setQuantity] = useState(1); // ✅ Add quantity state
  
//   const hasVariations = product.variations && 
//     typeof product.variations === 'object' && 
//     Object.keys(product.variations).length > 0;
  
//   const hasExtras = product.extras && 
//     Array.isArray(product.extras) && 
//     product.extras.length > 0;
  
//   const hasAddons = product.addons && 
//     Array.isArray(product.addons) && 
//     product.addons.length > 0;
  
//   const needsCustomization = hasVariations || hasExtras || hasAddons;
  
//   const isTracked = product.is_tracked || false;
//   const availableQty = product.available_stock;
//   const isOutOfStock = isTracked && !product.is_available;
//   const isLimitedStock = isTracked && product.is_available && availableQty !== null && availableQty <= 5;
  
//   const displayPrice = needsCustomization 
//     ? getLowestVariationPrice(product)
//     : parseFloat(product.price || product.flatPrice || 0);
  
//   // ✅ Get description (combo items or normal description)
//   const displayDescription = getComboItemsDisplay(product);
  
//   const handleCardClick = (e) => {
//     if (e.target.closest('.menu-app-add-btn') || e.target.closest('.menu-app-overlay-add-btn')) {
//       return;
//     }
    
//     if (product.isCombo) {
//       setShowCustomize(true);
//     } else if (onShowDetails) {
//       onShowDetails(product);
//     }
//   };
  
//   const handleDirectAdd = (e) => {
//     e?.stopPropagation();
    
//     if (isOutOfStock) {
//       alert(`❌ Sorry, ${product.name} is currently out of stock.`);
//       return;
//     }

//      // ⭐ ADD THIS CHECK
//   if (isTracked && availableQty && quantity > availableQty) {
//     alert(`❌ Only ${Math.floor(availableQty)} items available!`);
//     return;
//   }
//     if (!isUserLoggedIn) {
//       if (onShowAuthModal) {
//         onShowAuthModal();
//       } else {
//         alert('Please login to add items to cart');
//       }
//       return;
//     }
    
//     if (needsCustomization || product.isCombo) {
//       setShowCustomize(true);
//     } else {
//       const simpleProduct = {
//         ...product,
//         quantity: quantity,
//         variations: [],
//         extras: [],
//         addons: [],
//         flatPrice: displayPrice
//       };
      
//       onAddToCart(simpleProduct);
//     }
//   };
  
//   return (
//     <>
//       <div 
//         className={`menu-app-product-card ${isOutOfStock ? 'out-of-stock' : ''}`}
//         onClick={handleCardClick}
//         style={{ cursor: 'pointer' }}
//       >
//         <div className="menu-app-product-image-wrapper">
//           <img 
//             src={product.image} 
//             alt={product.name} 
//             className="menu-app-product-image"
//             onError={(e) => {
//               e.target.src = '/logo_transparent.png';
//             }}
//           />
          
//           <div className="menu-app-product-badge">
//             {product.isVeg !== undefined && (
//               <div className={`menu-app-badge ${product.isVeg ? 'veg' : 'non-veg'}`}>
//                 <div className={product.isVeg ? 'veg-dot' : 'non-veg-dot'}></div>
//               </div>
//             )}
//           </div>
          
//           <div className="menu-app-product-badge-right">
//             {isOutOfStock && (
//               <div className="menu-app-stock-badge out-of-stock">
//                 OUT OF STOCK
//               </div>
//             )}
            
//             {isLimitedStock && !isOutOfStock && (
//               <div className="menu-app-stock-badge limited-stock">
//                 Only {Math.floor(availableQty)} left
//               </div>
//             )}
            
//             {product.isCombo && !isOutOfStock && (
//               <div className="menu-app-stock-badge combo">
//                 Combo Offer
//               </div>
//             )}
            
//             {needsCustomization && !isOutOfStock && !product.isCombo && (
//               <div className="menu-app-stock-badge customizable">
//                 Customizable
//               </div>
//             )}
//           </div>
//         </div>
        
//         <div className="menu-app-product-info">
//           <h3 className="menu-app-product-name">{product.name}</h3>
          
//           {displayDescription && (
//             <p className="menu-app-product-description">{displayDescription}</p>
//           )}
          
//           <div className="menu-app-product-footer">
//             <span className="menu-app-product-price">
//               {needsCustomization && !product.isCombo && 'From '}₹{displayPrice.toFixed(2)}
//             </span>
            
//             <button 
//               onClick={handleDirectAdd}
//               className={`menu-app-add-btn ${isOutOfStock ? 'disabled' : ''}`}
//               disabled={isOutOfStock}
//             >
//               {isOutOfStock ? 'Out of Stock' : 'ADD'}
//             </button>
//           </div>
  
//         </div>
//       </div>
      
//       {(needsCustomization || product.isCombo) && (
//         <CustomizationModal
//           isOpen={showCustomize}
//           onClose={() => setShowCustomize(false)}
//           product={product}
//           onAddToCart={onAddToCart}
//         />
//       )}
//     </>
//   );
// }, (prevProps, nextProps) => {
//   return (
//     prevProps.product.id === nextProps.product.id &&
//     prevProps.product.is_available === nextProps.product.is_available &&
//     prevProps.product.available_stock === nextProps.product.available_stock
//   );
// });

// ProductCard.displayName = 'ProductCard';

// export default ProductCard;